import React, {
    useState,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useCallback,
} from "react";
import { useLazyQuery, useApolloClient } from "@apollo/react-hooks";
import { CSVLink } from "react-csv";
import moment from "moment";
import { QUERY_CUSTOMER_LIST } from "../../apollo/module-operations/customer";
import routes from "../../config/routes/routes";
import { headers_customers } from "../../config/constant";
import { Content } from "@/layout";
import { useMeta, useRowActions, useNotification } from "@/hooks";
import { currencyFormat } from "utils/utils";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    HeaderActions,
    Heading,
    Input,
    LoadingProgress,
    Panel,
    Table,
    Text,
} from "@/ui";
import { RowActionsMenu } from "../store/shared";
import { CcpaDeleteModal, useCcpaDeleteCustomer } from "./ccpa";
import ExportProgressModal from "./components/ExportProgressModal";
import styles from "./Customers.module.scss";

const ExportIcon = () => <Icon icon="document" />;
const RefreshIcon = () => <Icon icon="refresh" />;
const SearchIcon = () => <Icon icon="search" />;
const ClearSearchIcon = () => <Icon icon="close" />;
const CostumersAllIcon = () => <Icon icon="costumers-all" />;
const CostumersSelectedIcon = () => <Icon icon="costumers-selected" />;

const INITIAL_SELECTION_STATE = {
    ids: new Set(),
    version: 0,
};

const selectionReducer = (state, action) => {
    switch (action.type) {
        case "toggle": {
            const nextIds = new Set(state.ids);
            if (action.select) {
                nextIds.add(action.id);
            } else {
                nextIds.delete(action.id);
            }
            return {
                ids: nextIds,
                version: state.version + 1,
            };
        }
        case "setAll": {
            return {
                ids: new Set(action.ids),
                version: state.version + 1,
            };
        }
        case "reset":
            if (state.ids.size === 0) {
                return state;
            }
            return {
                ids: new Set(),
                version: state.version + 1,
            };
        default:
            return state;
    }
};

const Customers = () => {
    const { user } = useMeta();
    const notify = useNotification();
    const apolloClient = useApolloClient();

    const [fetchingMore, setFetchingMore] = useState(false);
    const [selectionState, dispatchSelection] = useReducer(
        selectionReducer,
        INITIAL_SELECTION_STATE,
    );
    const [searchValue, setSearchValue] = useState(null);
    const [currentFilter] = useState({
        value: "email",
        label: "Email",
    });
    const [isExportingAll, setIsExportingAll] = useState(false);
    const [exportAllData, setExportAllData] = useState([]);
    const [exportAllVersion, setExportAllVersion] = useState(0);
    const [exportSelectedVersion, setExportSelectedVersion] = useState(0);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);
    const [isCancelled, setIsCancelled] = useState(false);
    const exportAllLinkRef = useRef(null);
    const exportSelectedLinkRef = useRef(null);
    const cancelExportRef = useRef(false);

    const [
        listAccounts,
        {
            loading: loadingaccounts,
            data: accounts_data,
            networkStatus,
            refetch,
            fetchMore,
        },
    ] = useLazyQuery(QUERY_CUSTOMER_LIST, {
        notifyOnNetworkStatusChange: true,
        skip: user.loading || !user.accountId || !user.storeId,
        variables: {
            account_id: user.accountId,
            store_id: user.storeId,
        },
    });

    const { update } = useRowActions(
        [
            {
                query: QUERY_CUSTOMER_LIST,
                skip: user.loading || !user.accountId || !user.storeId,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                },
            },
        ],
        "",
        routes.CUSTOMER_DETAIL.route,
        "Customers",
    );

    const { openDelete, modalProps } = useCcpaDeleteCustomer({
        onCompleted: result => {
            if (result?.success) {
                refetch();
            }
        },
    });

    const next = cursor => {
        setFetchingMore(true);

        fetchMore({
            query: QUERY_CUSTOMER_LIST,
            variables: searchValue
                ? {
                      cursor: cursor,
                      account_id: user.accountId,
                      store_id: user.storeId,
                      account_filter: { email: searchValue },
                  }
                : {
                      cursor: cursor,
                      account_id: user.accountId,
                      store_id: user.storeId,
                  },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                const previousEntry = previousResult.listAccounts;
                const newCustomers = fetchMoreResult.listAccounts.accounts;
                const newCursor = fetchMoreResult.listAccounts.cursor;

                setFetchingMore(false);
                return {
                    listAccounts: {
                        cursor: newCursor,
                        accounts: [...previousEntry.accounts, ...newCustomers],
                        __typename: previousEntry.__typename,
                    },
                };
            },
        });
    };

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 56,
                minWidth: 56,
                renderCell: row => (
                    <span onClick={event => event.stopPropagation()}>
                        <RowActionsMenu
                            deleteLabel="Delete customer data"
                            label={
                                row.displayName || row.email || row.entity_id
                            }
                            onDelete={() =>
                                openDelete({
                                    email: row.email,
                                    id: row.entity_id,
                                    name: row.displayName,
                                })
                            }
                            onUpdate={() => update(row.entity_id)}
                        />
                    </span>
                ),
            },
            {
                id: "customer",
                header: "Customer",
                width: "25%",
                minWidth: 180,
                renderCell: row => (
                    <div className={styles.cellStack}>
                        <div className={styles.primaryCell}>
                            {row.displayName}
                        </div>
                        <div className={styles.secondaryCell}>{row.email}</div>
                    </div>
                ),
            },
            {
                id: "phone",
                header: "Phone",
                width: "10%",
                minWidth: 110,
                renderCell: row => row.phone,
            },
            {
                id: "address",
                header: "Address",
                width: "20%",
                minWidth: 160,
                renderCell: row => (
                    <div className={styles.cellStack}>
                        <div className={styles.primaryCell}>
                            {row.address1} {row.address2}
                        </div>
                        <div className={styles.secondaryCell}>
                            {row.city && `${row.city} ,`} {row.zip}
                        </div>
                    </div>
                ),
            },
            {
                id: "accept_marketing",
                header: "Opt In",
                width: "10%",
                minWidth: 90,
                renderCell: row => (
                    <Badge
                        variant={row.accept_marketing ? "primary" : "neutral"}
                    >
                        {row.accept_marketing ? "Yes" : "No"}
                    </Badge>
                ),
            },
            {
                id: "has_account",
                header: "Have Account",
                width: "10%",
                minWidth: 110,
                renderCell: row => (
                    <Badge variant={row.has_account ? "primary" : "neutral"}>
                        {row.has_account ? "Active" : "Inactive"}
                    </Badge>
                ),
            },
            {
                id: "created_at",
                header: "Created At",
                width: "15%",
                minWidth: 140,
                renderCell: row => (
                    <div className={styles.cellStack}>
                        <div className={styles.primaryCell}>
                            {row.createdAtDisplay}
                        </div>
                        <div className={styles.secondaryCell}>
                            {row.createdAtDisplayTime}
                        </div>
                    </div>
                ),
            },
        ],
        [openDelete, update],
    );

    const selection = useMemo(
        () => Array.from(selectionState.ids),
        [selectionState],
    );

    const _loading = (loadingaccounts || networkStatus === 4) && !fetchingMore;
    const rawCustomers =
        (accounts_data && accounts_data.listAccounts.accounts) || [];
    const _cursor = accounts_data && accounts_data.listAccounts.cursor;

    const hydratedCustomers = useMemo(() => {
        return rawCustomers.map(customer => {
            const createdMoment = customer.created_at
                ? moment(customer.created_at)
                : null;
            const updatedMoment = customer.updated_at
                ? moment(customer.updated_at)
                : null;
            const displayName = `${customer.first_name ?? ""} ${
                customer.last_name ?? ""
            }`
                .replace(/\s+/g, " ")
                .trim();

            return {
                ...customer,
                displayName,
                createdAtDisplay: createdMoment
                    ? createdMoment.format("MMMM Do YYYY")
                    : "",
                createdAtDisplayTime: createdMoment
                    ? createdMoment.format("HH:mm:ss")
                    : "",
                exportCreatedAt: createdMoment
                    ? createdMoment.format("MM/DD/YYYY HH:mm")
                    : "",
                exportUpdatedAt: updatedMoment
                    ? updatedMoment.format("MM/DD/YYYY HH:mm")
                    : "",
            };
        });
    }, [rawCustomers]);

    const serializeForExport = customer => {
        const createdMoment = customer.created_at
            ? moment(customer.created_at)
            : null;
        const updatedMoment = customer.updated_at
            ? moment(customer.updated_at)
            : null;
        const displayName = `${customer.first_name ?? ""} ${
            customer.last_name ?? ""
        }`
            .replace(/\s+/g, " ")
            .trim();

        const createdAtFormatted = createdMoment
            ? createdMoment.format("MM/DD/YYYY HH:mm")
            : "";
        const updatedAtFormatted = updatedMoment
            ? updatedMoment.format("MM/DD/YYYY HH:mm")
            : "";

        const dateOfBirthFormatted = customer.date_of_birth
            ? moment(customer.date_of_birth).format("YYYY-MM-DD")
            : "";

        const boolToText = value =>
            value === null || value === undefined
                ? ""
                : value
                ? "TRUE"
                : "FALSE";

        const activityLogsText = Array.isArray(customer.activity_logs)
            ? customer.activity_logs
                  .map(log => {
                      const parts = [];
                      if (log.action) parts.push(`action:${log.action}`);
                      if (log.user) parts.push(`user:${log.user}`);
                      if (log.status) parts.push(`status:${log.status}`);
                      if (log.timestamp)
                          parts.push(`timestamp:${log.timestamp}`);
                      return parts.join(" | ");
                  })
                  .filter(Boolean)
                  .join(" || ")
            : "";

        const notesText = Array.isArray(customer.notes)
            ? customer.notes.filter(Boolean).join(" | ")
            : "";

        const treezIdsText = Array.isArray(customer.treez_ids)
            ? customer.treez_ids
                  .map(t => {
                      if (!t) return "";
                      const store = t.store_id ? `store_id:${t.store_id}` : "";
                      const id = t.id ? `id:${t.id}` : "";
                      return [store, id].filter(Boolean).join(" | ");
                  })
                  .filter(Boolean)
                  .join(" || ")
            : "";

        // Stats used in Customer Detail UI (GeneralPane)
        const lifetimeSpend = Number(customer.total_order_expense || 0);
        const totalOrders = Array.isArray(customer.activity_logs)
            ? customer.activity_logs.filter(
                  log => log && log.action === "ORDER_COMPLETED",
              ).length
            : 0;
        const averageSpend = totalOrders > 0 ? lifetimeSpend / totalOrders : 0;

        return {
            // Campos originales del export
            email: customer.email,
            first_name: displayName,
            date_of_birth: dateOfBirthFormatted,
            last_name: customer.last_name ?? "",
            phone: customer.phone ?? "",
            created_at: createdAtFormatted,
            update_at: updatedAtFormatted,
            // Additional requested fields
            accept_marketing: boolToText(customer.accept_marketing),
            active: boolToText(customer.active || false),
            has_account: boolToText(customer.has_account),
            address1: customer.address1 ?? "",
            address2: customer.address2 ?? "",
            city: customer.city ?? "",
            city_code: customer.city_code ?? "",
            country: customer.country ?? "",
            country_code: customer.country_code ?? "",
            latitude: customer.latitude ?? "",
            longitude: customer.longitude ?? "",
            province: customer.province ?? "",
            province_code: customer.province_code ?? "",
            zip: customer.zip ?? "",
            total_order_expense: currencyFormat(lifetimeSpend),
            average_spend: currencyFormat(averageSpend),
            total_orders: totalOrders,
            loyalty_points: customer.loyalty_points ?? "",
            photo: customer.photo ?? "",
            notes: notesText,
            driver_license_id: customer.driver_license_id ?? "",
            driver_license_photo: customer.driver_license_photo ?? "",
            tags: customer.tags ?? "",
            treez_ids: treezIdsText,
            entity_id: customer.entity_id ?? "",
            leaf_logic_id: customer.leaf_logic_id ?? "",
            key: customer.key ?? "",
            activity_logs: activityLogsText,
            checkin_from_dispensary: boolToText(
                customer.checkin_from_dispensary,
            ),
        };
    };

    const customersById = useMemo(() => {
        return hydratedCustomers.reduce((acc, customer) => {
            acc[String(customer.entity_id)] = customer;
            return acc;
        }, {});
    }, [hydratedCustomers]);

    const getCustomersToExport = useCallback(() => {
        if (!selection.length) {
            return [];
        }

        return selection
            .map(id => customersById[String(id)])
            .filter(Boolean)
            .map(serializeForExport);
    }, [customersById, selection]);

    const formatCustomersForExport = useCallback(customers => {
        return customers.map(serializeForExport);
    }, []);

    const handleExportSelected = useCallback(() => {
        if (!selection.length) {
            notify("Select at least one customer to export.", "info");
            return;
        }

        setExportSelectedVersion(prev => prev + 1);

        requestAnimationFrame(() => {
            if (exportSelectedLinkRef.current?.link) {
                exportSelectedLinkRef.current.link.click();
            }
        });
    }, [notify, selection]);

    const fetchAllCustomers = useCallback(async () => {
        if (!accounts_data || !accounts_data.listAccounts) {
            return [];
        }

        const aggregated = [...accounts_data.listAccounts.accounts];
        const seenIds = new Set(aggregated.map(customer => customer.entity_id));
        let cursor = accounts_data.listAccounts.cursor;
        const cursorHistory = new Set();
        let pageCount = 1; // Start with 1 for the initial page

        // Estimate total pages (we'll update as we go)
        let estimatedTotal = 1;

        while (cursor && !cursorHistory.has(cursor)) {
            // Check if cancelled
            if (cancelExportRef.current) {
                return null;
            }

            cursorHistory.add(cursor);
            pageCount++;

            const { data } = await apolloClient.query({
                query: QUERY_CUSTOMER_LIST,
                variables: {
                    account_id: user.accountId,
                    store_id: user.storeId,
                    cursor,
                },
                fetchPolicy: "network-only",
            });

            if (!data || !data.listAccounts) {
                break;
            }

            const { accounts: newAccounts, cursor: nextCursor } =
                data.listAccounts;

            newAccounts.forEach(account => {
                if (!seenIds.has(account.entity_id)) {
                    aggregated.push(account);
                    seenIds.add(account.entity_id);
                }
            });

            // Update progress (estimate: if there's a next cursor, we're not done)
            if (nextCursor) {
                estimatedTotal = pageCount + 1; // Estimate one more page
                const progress = Math.min(
                    Math.round((pageCount / estimatedTotal) * 90),
                    90,
                );
                setExportProgress(progress);
            } else {
                // Set to 95% when done fetching, 100% will be set after formatting
                setExportProgress(95);
            }

            cursor = nextCursor;
        }

        return aggregated;
    }, [accounts_data, apolloClient, user.accountId, user.storeId]);

    const handleExportAll = useCallback(async () => {
        if (isExportingAll) {
            return;
        }

        if (!accounts_data || !accounts_data.listAccounts?.accounts?.length) {
            notify("There are no customers to export.", "info");
            return;
        }

        // Reset states and show modal
        cancelExportRef.current = false;
        setIsCancelled(false);
        setExportProgress(0);
        setShowExportModal(true);
        setIsExportingAll(true);

        try {
            const allCustomers = await fetchAllCustomers();

            // Check if cancelled
            if (cancelExportRef.current || allCustomers === null) {
                setIsCancelled(true);
                setShowExportModal(false);
                setIsExportingAll(false);
                setExportProgress(0);
                return;
            }

            // Set progress to 100% first to show completion
            setExportProgress(100);

            // Wait a moment to show the 100% progress
            await new Promise(resolve => setTimeout(resolve, 300));

            // Check again if cancelled during the delay
            if (cancelExportRef.current) {
                setIsCancelled(true);
                setShowExportModal(false);
                setIsExportingAll(false);
                setExportProgress(0);
                return;
            }

            const formattedData = formatCustomersForExport(allCustomers);

            if (!formattedData.length) {
                notify("There are no customers to export.", "info");
                setShowExportModal(false);
                setIsExportingAll(false);
                setExportProgress(0);
                return;
            }

            setExportAllData(formattedData);
            setExportAllVersion(prev => prev + 1);

            // Wait a bit more to show completion before closing
            await new Promise(resolve => setTimeout(resolve, 500));

            // Close modal after showing 100%
            setShowExportModal(false);
            setIsExportingAll(false);
            setExportProgress(0);
        } catch (error) {
            console.error("Error exporting all customers:", error);
            notify(
                "We couldn't prepare the export. Please try again in a moment.",
                "error",
            );
            setShowExportModal(false);
            setIsExportingAll(false);
            setExportProgress(0);
        }
    }, [
        accounts_data,
        fetchAllCustomers,
        formatCustomersForExport,
        isExportingAll,
        notify,
    ]);

    const handleCancelExport = useCallback(() => {
        cancelExportRef.current = true;
        setIsCancelled(true);
        setShowExportModal(false);
        setIsExportingAll(false);
        setExportProgress(0);
        notify("Export cancelled.", "info");
    }, [notify]);

    useEffect(() => {
        !user.loading && listAccounts();
    }, [user.loading]);

    useEffect(() => {
        if (
            exportAllVersion > 0 &&
            exportAllData.length > 0 &&
            exportAllLinkRef.current?.link
        ) {
            // Small delay to ensure the CSVLink component has updated
            const timer = setTimeout(() => {
                if (exportAllLinkRef.current?.link) {
                    exportAllLinkRef.current.link.click();
                }
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [exportAllVersion, exportAllData]);

    const handleSearch = () => {
        const trimmedValue = searchValue ? searchValue.trim() : "";
        if (!trimmedValue) {
            listAccounts();
            return;
        }

        listAccounts({
            fetchPolicy: "network-only",
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                account_filter: {
                    [`${currentFilter.value}`]: trimmedValue,
                },
            },
        });
    };

    const headerActions = [
        {
            id: "refresh",
            label: "Refresh",
            icon: <RefreshIcon />,
            disabled: isExportingAll,
            onClick: () => refetch(),
            variant: "secondary",
        },
        {
            id: "export",
            label: isExportingAll ? "...exporting" : "Export",
            icon: <ExportIcon />,
            disabled: isExportingAll,
            variant: "primary",
            menuItems: [
                {
                    id: "export-all",
                    label: isExportingAll ? "Preparing..." : "All Costumers",
                    icon: <CostumersAllIcon />,
                    disabled: isExportingAll || _loading,
                    onSelect: () => {
                        if (isExportingAll || _loading) return;
                        handleExportAll();
                    },
                },
                {
                    id: "export-selected",
                    label: "Costumers Selected",
                    icon: <CostumersSelectedIcon />,
                    disabled: !selection.length,
                    onSelect: () => {
                        if (!selection.length) return;
                        handleExportSelected();
                    },
                },
            ],
        },
    ];

    return (
        <>
            <Content>
                <Panel
                    className={`customers__content ${styles.root}`}
                    header={
                        <div className={styles.headerBar}>
                            <div className={styles.headerText}>
                                <Heading level={4}>Customers</Heading>
                                <Text tone="secondary" variant="body-md">
                                    All customers availables in the system
                                </Text>
                            </div>
                            <HeaderActions
                                actions={headerActions}
                                aria-label="Customers actions"
                            />
                        </div>
                    }
                    showHeaderDivider
                >
                    <div className={styles.searchBar}>
                        {/* Filter field dropdown (only Email active for now):
                            <DropdownMenu>
                                {FilterOptions.map((f, index) => (
                                    <DropdownItem
                                        key={index}
                                        onClick={() => {
                                            setCurrentFilter(f);
                                            setSearchValue("");
                                        }}
                                    >
                                        {f.label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        */}
                        <div className={styles.searchField}>
                            <Input
                                density="compact"
                                disabled={isExportingAll}
                                fullWidth
                                iconRight={
                                    searchValue ? (
                                        <button
                                            aria-label="Clear search"
                                            className={styles.searchClear}
                                            disabled={isExportingAll}
                                            onClick={() => {
                                                setSearchValue(null);
                                                listAccounts();
                                            }}
                                            type="button"
                                        >
                                            <ClearSearchIcon />
                                        </button>
                                    ) : null
                                }
                                label="Search customers by email"
                                labelBehavior="inline"
                                name="search"
                                onChange={e => {
                                    const value = e.target.value;
                                    const newValue = value.length
                                        ? value
                                        : null;
                                    const wasCleared = !newValue && searchValue;
                                    setSearchValue(newValue);

                                    // If the input is cleared, reload the list without filters
                                    if (wasCleared) {
                                        listAccounts();
                                    }
                                }}
                                onKeyDown={e => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSearch();
                                    }
                                }}
                                placeholder="Search customers by email"
                                value={searchValue ?? ""}
                            />
                        </div>
                        <Button
                            aria-label="Search"
                            className={styles.searchButton}
                            disabled={isExportingAll}
                            iconLeft={<SearchIcon />}
                            onClick={handleSearch}
                            size="md"
                            type="button"
                            variant="secondary"
                        >
                            <span className={styles.searchButtonLabel}>
                                Search
                            </span>
                        </Button>
                    </div>

                    {_loading ? (
                        <div className={styles.loading}>
                            <LoadingProgress
                                indeterminate
                                indicator="circular"
                                size="sm"
                                title="Loading"
                            />
                        </div>
                    ) : (
                        <Table
                            columns={columns}
                            emptyMessage="No customers found"
                            getRowId={row => String(row.entity_id)}
                            onRowClick={row => update(row.entity_id)}
                            onSelectedRowIdsChange={rowIds => {
                                dispatchSelection({
                                    type: "setAll",
                                    ids: rowIds,
                                });
                            }}
                            rows={hydratedCustomers}
                            selectable
                            selectedRowIds={selection.map(String)}
                            stickyHeader={false}
                        />
                    )}

                    {hydratedCustomers.length > 1 ? (
                        <div className={styles.loadMore}>
                            <Button
                                disabled={!_cursor || fetchingMore}
                                iconLeft={
                                    !fetchingMore ? <RefreshIcon /> : undefined
                                }
                                loading={fetchingMore}
                                onClick={() => next(_cursor)}
                                type="button"
                                variant="secondary"
                            >
                                Show more
                            </Button>
                        </div>
                    ) : null}
                </Panel>
            </Content>
            <CSVLink
                className={styles.hiddenLink}
                data={exportAllData}
                filename="customers-all.csv"
                headers={headers_customers}
                key={`customers-all-${exportAllVersion}`}
                ref={exportAllLinkRef}
                target="_blank"
            />
            <CSVLink
                className={styles.hiddenLink}
                data={getCustomersToExport()}
                filename="customers.csv"
                headers={headers_customers}
                key={`customers-selected-${exportSelectedVersion}`}
                ref={exportSelectedLinkRef}
                target="_blank"
            />
            <ExportProgressModal
                isCancelled={isCancelled}
                isOpen={showExportModal}
                onCancel={handleCancelExport}
                onClose={handleCancelExport}
                progress={exportProgress}
            />
            <CcpaDeleteModal {...modalProps} />
        </>
    );
};

export default Customers;
